// src/components/QuizRunner.jsx
import React from "react";

export function QuizRunner({
  quizName,
  questions,
  current,
  timer,
  isAnswered,
  selectedOptions,
  onSelect,
  onSubmit,
}) {
  const q = questions[current];

  // figure out which indexes are correct
  const correctIdx = q.correct
    .split(",")
    .map((l) => ({ A: 0, B: 1, C: 2, D: 3, E: 4 }[l.trim()]));

  const isMultiple = correctIdx.length > 1;

  // build a list of only the non‑empty options, keeping their original idx
  const optionItems = q.options
    .map((text, idx) => ({
      text: String(text || "").trim(),
      idx,
    }))
    .filter(({ text }) => text !== "");

  return (
    <div className="max-w-3xl mx-auto p-6 bg-white rounded-lg shadow-md space-y-6">
      {/* Quiz Title */}
      <h1 className="text-2xl font-bold text-gray-800">{quizName}</h1>

      {/* Question Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold text-gray-700">
          Question {current + 1} of {questions.length}
        </h2>
        <span className="text-sm font-medium text-gray-600">
          Time left: {timer}s
        </span>
      </div>

      {/* Question Text */}
      <p className="text-gray-800">{q.question}</p>

      {/* Single vs Multiple Indicator */}
      <p className="italic text-sm text-indigo-600">
        {isMultiple
          ? "Multiple answer question — select all that apply."
          : "Single answer question — select one option."}
      </p>

      {/* Options List */}
      <div className="space-y-3">
        {optionItems.map(({ text, idx }) => {
          const disabled = isAnswered;
          const selected = selectedOptions.includes(idx);
          let btnClass =
            "block w-full text-left p-3 border rounded-lg transition";

          if (isAnswered) {
            if (correctIdx.includes(idx)) {
              btnClass += " bg-green-100 border-green-500";
            } else if (selected) {
              btnClass += " bg-red-100 border-red-500";
            } else {
              btnClass += " opacity-50";
            }
          } else {
            btnClass += selected
              ? " bg-blue-100 border-blue-400"
              : " hover:bg-gray-100 border-gray-300";
          }

          return (
            <button
              key={idx}
              className={btnClass}
              disabled={disabled}
              onClick={() => onSelect(idx)}
            >
              <span className="font-medium mr-2">
                {String.fromCharCode(65 + idx)}.
              </span>
              {text}
            </button>
          );
        })}
      </div>

      {/* Submit Button */}
      {!isAnswered && selectedOptions.length > 0 && (
        <button
          onClick={onSubmit}
          className="mt-4 w-full bg-indigo-600 text-white py-2 rounded hover:bg-indigo-700 transition"
        >
          Submit Answer
        </button>
      )}

      {/* Feedback */}
      {isAnswered && (
        <p
          className={`mt-4 text-center font-semibold ${
            selectedOptions.length === correctIdx.length &&
            selectedOptions.every((i) => correctIdx.includes(i))
              ? "text-green-600"
              : "text-red-600"
          }`}
        >
          {selectedOptions.length === correctIdx.length &&
          selectedOptions.every((i) => correctIdx.includes(i))
            ? "Correct!"
            : "Incorrect"}
        </p>
      )}
    </div>
  );
}
